/**
 * Presentation-layer reordering for conversational onboarding assistant messages.
 *
 * Mastra streams each assistant turn as text deltas and UI tool chunks over SSE.
 * useMastraChat accumulates those parts in arrival order, which often differs from
 * the narrative order we want in chat (intro → widget → follow-up).
 *
 * The agent is instructed to emit in that narrative order (see ConversationalMMAgent
 * and SproutAgent "UI and text ordering", plus branch skills such as goals-debt
 * "three beats"), but tool calls can land before or after related copy, intro text for tables sometimes
 * arrives after the table tools, and the model may merge reaction + next question
 * into a single text block.
 *
 * This module reorders parts at display time only — it does not change what Mastra
 * stores or what the agent receives. Used by OnboardingChatContainer, useMastraChat,
 * convertThreadMessagesToChatMessages, and assistantMessageLogoIcon.
 */
import { WidgetType } from '@fruitful/types';

import type { ChatMessage, TextContent, UIContent } from '../types/ChatTypes';

export type AssistantContentPart = TextContent | UIContent;

const hasWidget = (
    parts: AssistantContentPart[],
    widget: WidgetType,
): boolean =>
    parts.some((part) => part.type === 'ui' && part.widget === widget);

/**
 * Detect a text fragment that was split mid-word by a tool call boundary.
 *
 * When the LLM streams "Let" → tool call → "'s shift to…", the pre-UI text
 * is an orphan fragment ("Let") and the post-UI text starts with a contraction
 * suffix ("'s …"). Merge them so the full sentence appears after the widget.
 */
const isOrphanedFragment = (text: string): boolean => {
    const trimmed = text.trim();
    if (trimmed.length === 0) {
        return true;
    }
    const lastChar = trimmed[trimmed.length - 1] ?? '';
    const endsWithPunctuation = /[.!?:;,\-—]$/.test(lastChar);
    return !endsWithPunctuation && trimmed.length < 20;
};

/**
 * Fallback when the goal-summary flow merges two beats into one text part.
 *
 * The agent should stream (1) a brief reaction, (2) show-goal-summary-card, then
 * (3) the next question as separate beats. When it combines reaction + income
 * question into one streamed text block, split on paragraph breaks first, then
 * single newlines, so the card can sit between them.
 */
export const splitIntroAndFollowUpText = (
    text: string,
): { lead: string; followUp: string } | null => {
    const trimmed = text.trim();
    if (trimmed === '') {
        return null;
    }

    const byParagraph = trimmed
        .split(/\n\n+/)
        .map((segment) => segment.trim())
        .filter(Boolean);

    if (byParagraph.length >= 2) {
        return {
            lead: byParagraph[0] ?? '',
            followUp: byParagraph.slice(1).join('\n\n'),
        };
    }

    const lines = trimmed
        .split(/\n/)
        .map((line) => line.trim())
        .filter(Boolean);

    if (lines.length >= 2) {
        return {
            lead: lines[0] ?? '',
            followUp: lines.slice(1).join('\n'),
        };
    }

    return null;
};

/**
 * Debt / bills confirmation: intro text → InlineDataTable + DraftedMessageButton → trailing text.
 *
 * When the agent emits intro copy after the table tools (no pre-UI text, but post-UI text
 * exists), treat that post-UI text as the intro and move it above the widgets.
 */
const reorderDebtConfirmationParts = (
    parts: AssistantContentPart[],
): AssistantContentPart[] => {
    const firstUiIndex = parts.findIndex((part) => part.type === 'ui');
    if (firstUiIndex === -1) {
        return parts;
    }

    const uiParts = parts.filter((part) => part.type === 'ui');
    const preUiText = parts
        .slice(0, firstUiIndex)
        .filter((part) => part.type === 'text');
    const postUiText = parts
        .slice(firstUiIndex + 1)
        .filter((part) => part.type === 'text');

    if (preUiText.length === 0 && postUiText.length > 0) {
        return [...postUiText, ...uiParts];
    }

    const allPreText = preUiText.map((p) => p.text).join('');
    if (isOrphanedFragment(allPreText) && postUiText.length > 0) {
        const firstPost = postUiText[0] as TextContent;
        const merged: TextContent = {
            type: 'text',
            text: allPreText + firstPost.text,
        };
        return [merged, ...uiParts, ...postUiText.slice(1)];
    }

    return [...preUiText, ...uiParts, ...postUiText];
};

/**
 * Goal summary card: reaction text → GoalSummaryCard → next question.
 *
 * Handles three agent quirks:
 * - Normal: reaction streamed before the card tool call (preUiText present).
 * - Merged beats: reaction + follow-up in one post-UI text part → splitIntroAndFollowUpText.
 * - Split beats: two+ post-UI text parts → first is reaction, rest is follow-up.
 *
 * otherUi keeps any co-emitted widgets (e.g. DraftedMessageButton) after the card.
 */
const reorderGoalSummaryCardParts = (
    parts: AssistantContentPart[],
): AssistantContentPart[] => {
    const goalCardIndex = parts.findIndex(
        (part) =>
            part.type === 'ui' && part.widget === WidgetType.GoalSummaryCard,
    );

    if (goalCardIndex === -1) {
        return parts;
    }

    // A turn can chain the goal-summary beat with a following widget beat — e.g.
    // the debt fast path (reaction + goal card) immediately followed by the bills
    // opener (intro + table + confirmation). That trailing beat arrives already
    // ordered, but the single-beat logic below would treat its intro copy as the
    // card's follow-up and hoist it below the table. Scope this strategy to the
    // goal-card beat only (everything up to the next widget after the card) and
    // hand the rest back to the dispatcher so it gets its own strategy.
    const nextUiAfterCard = parts.findIndex(
        (part, index) => index > goalCardIndex && part.type === 'ui',
    );
    if (nextUiAfterCard !== -1) {
        const head = reorderGoalSummaryCardParts(
            parts.slice(0, nextUiAfterCard),
        );
        const tail = reorderAssistantContentParts(parts.slice(nextUiAfterCard));
        return [...head, ...tail];
    }

    const goalCard = parts[goalCardIndex] as UIContent;
    const preUiText = parts
        .slice(0, goalCardIndex)
        .filter((part) => part.type === 'text');
    const postUiText = parts
        .slice(goalCardIndex + 1)
        .filter((part) => part.type === 'text');
    const otherUi = parts.filter(
        (part) =>
            part.type === 'ui' && part.widget !== WidgetType.GoalSummaryCard,
    );

    if (preUiText.length > 0 && postUiText.length === 0) {
        if (preUiText.length === 1) {
            const onlyText = preUiText[0] as TextContent;
            const split = splitIntroAndFollowUpText(onlyText.text);
            if (split != null) {
                return [
                    { type: 'text', text: split.lead },
                    goalCard,
                    ...otherUi,
                    { type: 'text', text: split.followUp },
                ];
            }
        }
        return [...preUiText, goalCard, ...otherUi];
    }

    if (preUiText.length > 0) {
        const allPreText = preUiText.map((p) => p.text).join('');
        if (isOrphanedFragment(allPreText) && postUiText.length > 0) {
            const firstPost = postUiText[0] as TextContent;
            const merged: TextContent = {
                type: 'text',
                text: allPreText + firstPost.text,
            };
            const remainingPost = postUiText.slice(1);
            const split = splitIntroAndFollowUpText(merged.text);
            if (split != null) {
                return [
                    { type: 'text', text: split.lead },
                    goalCard,
                    ...otherUi,
                    { type: 'text', text: split.followUp },
                    ...remainingPost,
                ];
            }
            return [merged, goalCard, ...otherUi, ...remainingPost];
        }
        return [...preUiText, goalCard, ...otherUi, ...postUiText];
    }

    if (postUiText.length === 1) {
        const onlyText = postUiText[0] as TextContent;
        const split = splitIntroAndFollowUpText(onlyText.text);
        if (split != null) {
            return [
                { type: 'text', text: split.lead },
                goalCard,
                { type: 'text', text: split.followUp },
                ...otherUi,
            ];
        }

        return [onlyText, goalCard, ...otherUi];
    }

    if (postUiText.length >= 2) {
        return [
            postUiText[0] as TextContent,
            goalCard,
            ...postUiText.slice(1),
            ...otherUi,
        ];
    }

    return parts;
};

/**
 * Intake summary (AhaMoment widget): intro explainer → money map → wrap-up.
 *
 * The agent should stream (1) an explainer of what the money map is, (2) show-intake-summary,
 * then (3) collaborative wrap-up text. Handles cases where the widget arrives before
 * or after the text parts due to streaming order.
 */
const reorderAhaMomentParts = (
    parts: AssistantContentPart[],
): AssistantContentPart[] => {
    const ahaMomentIndex = parts.findIndex(
        (part) => part.type === 'ui' && part.widget === WidgetType.AhaMoment,
    );

    if (ahaMomentIndex === -1) {
        return parts;
    }

    const ahaMoment = parts[ahaMomentIndex] as UIContent;
    const preUiText = parts
        .slice(0, ahaMomentIndex)
        .filter((part) => part.type === 'text');
    const postUiText = parts
        .slice(ahaMomentIndex + 1)
        .filter((part) => part.type === 'text');
    const otherUi = parts.filter(
        (part) => part.type === 'ui' && part.widget !== WidgetType.AhaMoment,
    );

    if (preUiText.length > 0) {
        return [...preUiText, ahaMoment, ...otherUi, ...postUiText];
    }

    if (postUiText.length === 1) {
        const onlyText = postUiText[0] as TextContent;
        const split = splitIntroAndFollowUpText(onlyText.text);
        if (split != null) {
            return [
                { type: 'text', text: split.lead },
                ahaMoment,
                ...otherUi,
                { type: 'text', text: split.followUp },
            ];
        }

        return [onlyText, ahaMoment, ...otherUi];
    }

    if (postUiText.length >= 2) {
        return [
            postUiText[0] as TextContent,
            ahaMoment,
            ...otherUi,
            ...postUiText.slice(1),
        ];
    }

    return parts;
};

/** Spend + goals: intro copy before the slider; SpendGoals widget always last. */
const reorderSpendGoalsParts = (
    parts: AssistantContentPart[],
): AssistantContentPart[] => {
    const spendGoalsIndex = parts.findIndex(
        (part) => part.type === 'ui' && part.widget === WidgetType.SpendGoals,
    );

    if (spendGoalsIndex === -1) {
        return parts;
    }

    const spendGoals = parts[spendGoalsIndex] as UIContent;
    const textParts = parts.filter(
        (part): part is TextContent => part.type === 'text',
    );
    const otherUi = parts.filter(
        (part) => part.type === 'ui' && part.widget !== WidgetType.SpendGoals,
    );

    return [...textParts, ...otherUi, spendGoals];
};

const SPROUT_PRIMARY_WIDGETS: ReadonlySet<WidgetType> = new Set([
    WidgetType.Chart,
    WidgetType.FreeToSpend,
    WidgetType.MoneyMap,
    WidgetType.IncomeSplit,
    WidgetType.Transactions,
    WidgetType.AccountBalances,
    WidgetType.CardDetails,
]);

const TRAILING_ACTION_WIDGETS: ReadonlySet<WidgetType> = new Set([
    WidgetType.EmailSupport,
    WidgetType.SendToGuide,
]);

const isSproutPrimaryWidget = (part: AssistantContentPart): part is UIContent =>
    part.type === 'ui' && SPROUT_PRIMARY_WIDGETS.has(part.widget);

/**
 * Generic intro → primary widget → follow-up ordering for Sprout widgets.
 *
 * Handles widget-first SSE arrival, text-first arrival with the widget last,
 * and merged intro/follow-up text split around the widget.
 */
export const reorderIntroWidgetFollowUpParts = (
    parts: AssistantContentPart[],
): AssistantContentPart[] => {
    const primaryWidgetIndex = parts.findIndex(isSproutPrimaryWidget);
    if (primaryWidgetIndex === -1) {
        return parts;
    }

    const primaryWidget = parts[primaryWidgetIndex] as UIContent;
    const otherUi = parts.filter(
        (part, index) => part.type === 'ui' && index !== primaryWidgetIndex,
    );
    const textParts = parts.filter(
        (part): part is TextContent => part.type === 'text',
    );

    if (textParts.length === 0) {
        return parts;
    }

    if (textParts.length === 1) {
        const onlyText = textParts[0];
        if (onlyText == null) {
            return parts;
        }

        const split = splitIntroAndFollowUpText(onlyText.text);
        if (split != null) {
            return [
                { type: 'text', text: split.lead },
                primaryWidget,
                { type: 'text', text: split.followUp },
                ...otherUi,
            ];
        }

        return [
            { type: 'text', text: onlyText.text },
            primaryWidget,
            ...otherUi,
        ];
    }

    return [
        textParts[0] as TextContent,
        primaryWidget,
        ...textParts.slice(1),
        ...otherUi,
    ];
};

const isTrailingActionWidget = (
    part: AssistantContentPart,
): part is UIContent =>
    part.type === 'ui' && TRAILING_ACTION_WIDGETS.has(part.widget);

/**
 * Email support / send-to-guide CTAs always render after all copy, and only
 * the latest card of each type is kept.
 *
 * Those tools often arrive over SSE before the drafted message text, which
 * would otherwise paint the button above the prose. Processor retries and
 * repeated show-* calls can also emit several drafts in one turn; the live
 * stream appends each `data-ui` chunk, while persisted Grove transcripts
 * typically keep a single part. Keep the last draft so member chat matches.
 */
const moveTrailingActionWidgetsLast = (
    parts: AssistantContentPart[],
): AssistantContentPart[] => {
    if (!parts.some(isTrailingActionWidget)) {
        return parts;
    }

    const lastIndexByWidget = new Map<WidgetType, number>();
    for (let index = 0; index < parts.length; index += 1) {
        const part = parts[index];
        if (part != null && isTrailingActionWidget(part)) {
            lastIndexByWidget.set(part.widget, index);
        }
    }

    const rest: AssistantContentPart[] = [];
    const trailing: UIContent[] = [];
    for (let index = 0; index < parts.length; index += 1) {
        const part = parts[index];
        if (part == null) {
            continue;
        }

        if (isTrailingActionWidget(part)) {
            if (lastIndexByWidget.get(part.widget) === index) {
                trailing.push(part);
            }
            continue;
        }

        rest.push(part);
    }

    return [...rest, ...trailing];
};

export const isAssistantTurnAwaitingWidget = (
    message: ChatMessage,
): boolean => {
    if (message.role !== 'assistant' || message.isStreaming !== true) {
        return false;
    }

    const hasUi = message.content.some((part) => part.type === 'ui');
    if (hasUi) {
        return false;
    }

    return message.content.some(
        (part) => part.type === 'text' && part.text.trim().length > 0,
    );
};

export const isAssistantTurnWaitingForFirstToken = (
    message: ChatMessage,
): boolean => {
    if (message.role !== 'assistant' || message.isStreaming !== true) {
        return false;
    }

    return !message.content.some(
        (part) =>
            (part.type === 'text' && part.text.trim().length > 0) ||
            part.type === 'ui',
    );
};

export const getAssistantDisplayContent = (message: {
    role: string;
    content: AssistantContentPart[];
}): AssistantContentPart[] =>
    message.role === 'assistant'
        ? reorderAssistantContentParts(message.content)
        : message.content;

/**
 * Pick a widget-specific reorder strategy for mid-flow UI patterns.
 *
 * Dispatch order matters: GoalSummaryCard is checked before InlineDataTable because
 * both can appear in debt flows. AhaMoment (intake summary / money map) is checked
 * last as it only appears at the end of the conversation.
 *
 * - GoalSummaryCard: reaction → card → follow-up question
 * - SpendGoals: intro text → other UI → slider
 * - InlineDataTable: intro → table/button → trailing text
 * - AhaMoment: explainer → money map → collaborative wrap-up
 * - Sprout widgets (Chart, FreeToSpend, etc.): intro → widget → follow-up
 * - EmailSupport / SendToGuide: always last, below all copy; latest of each type only
 */
export const reorderAssistantContentParts = (
    parts: AssistantContentPart[],
): AssistantContentPart[] => {
    let reordered: AssistantContentPart[];

    if (hasWidget(parts, WidgetType.GoalSummaryCard)) {
        reordered = reorderGoalSummaryCardParts(parts);
    } else if (hasWidget(parts, WidgetType.SpendGoals)) {
        reordered = reorderSpendGoalsParts(parts);
    } else if (hasWidget(parts, WidgetType.InlineDataTable)) {
        reordered = reorderDebtConfirmationParts(parts);
    } else if (hasWidget(parts, WidgetType.AhaMoment)) {
        reordered = reorderAhaMomentParts(parts);
    } else {
        reordered = reorderIntroWidgetFollowUpParts(parts);
    }

    return moveTrailingActionWidgetsLast(reordered);
};

/**
 * During streaming, hide widgets until leading copy exists in the reordered layout.
 *
 * Prevents tables/cards from flashing above orphaned text while the agent is still
 * streaming the intro. useMastraChat calls this via buildAssistantContentFromParts
 * with includeUi: false until this returns true.
 */
export const shouldShowUiWhileStreaming = (
    parts: AssistantContentPart[],
): boolean => {
    const ordered = reorderAssistantContentParts(parts);
    const firstUiIndex = ordered.findIndex((part) => part.type === 'ui');

    if (firstUiIndex <= 0) {
        return false;
    }

    return ordered
        .slice(0, firstUiIndex)
        .some((part) => part.type === 'text' && part.text.trim().length > 0);
};
